import type { UseFormOptions } from "../types.js";

/**
 * Make an API request.
 *
 * @template T The type of the response.
 * @param formOptions The options for the form.
 * @param requestBody The body of the request.
 * @returns The response from the API.
 */
export async function makeAPIRequest<T extends object>(
	formOptions: Pick<UseFormOptions, "endpoint" | "headers">,
	requestBody: string | object,
): Promise<T> {
	const body =
		typeof requestBody === "string" ? requestBody : JSON.stringify(requestBody);
	if (typeof formOptions.endpoint === "function") {
		return await formOptions.endpoint(body);
	}
	const response = await fetch(formOptions.endpoint, {
		method: "POST",
		body,
		headers: formOptions.headers || { "Content-Type": "application/json" },
	});
	if (!response.ok) {
		const error = await response.json();
		if (error && typeof error === "object" && "data" in error) {
			throw new Error(`${error.data}`);
		}
	}
	const responseParsed = (await response.json()) as
		| { success: false; data: string }
		| { success: true; data: T };

	if (!responseParsed.success) {
		throw new Error(responseParsed.data);
	}

	return responseParsed.data;
}
